import { html, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { SvgUtility } from '~/ui/utils';
import { formatDateToDDMMYY } from '~/modules/utils/date-formater';
import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './der-tailormade-booking-request.styles';
import { RoundtripOfferViewModel } from '~/tailormade/interfaces';
import DERModuleElement from '~/modules/utils/der-module-element';

import '~/ui/components/button';
import '~/ui/components/checkbox';
import '~/ui/components/input';
import '~/ui/components/phone-input';
import '~/ui/components/radio';
import '~/ui/components/radio-group';
import '~/ui/components/textarea';
import '~/ui/objects/module/body';
import '~/ui/objects/module/module';

import './sales-sites';
import { SalesSiteResponse, Environment } from './booking-request.interface';
import { Variant } from '~/modules/der-tailormade-booking-request/enum';
import { withOpportunities } from '~/modules/utils/opportunity-mixin';

enum VIEW_MODE {
    DEFAULT = 'default',
    CONFIRMATION = 'confirmation',
}

export class BookingRequestComponent extends withOpportunities(DERModuleElement) {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: Boolean, attribute: 'confirmation' })
    confirmation: boolean = false;

    @property({ type: String, attribute: 'contact-language' })
    contactLanguage: string = '';

    @property({ type: String, attribute: 'form-name' })
    formName: string = 'Round Trip Offer Request Form';

    @property({ type: String, attribute: 'identifier' })
    identifier: string = '';

    @property({ type: Object, attribute: 'offer-data' })
    offerData!: RoundtripOfferViewModel;

    @property({ type: String, attribute: 'product-key' })
    productKey: string = '';

    @property({ type: String, attribute: 'sales-site-url' })
    salesSiteUrl: string = 'https://dtch-tailormade.azurewebsites.net/api/branch-get-salessites';

    @property({ type: String, attribute: 'submit-url' })
    submitUrl: string = 'https://dtch-tailormade.azurewebsites.net/api/booking-request-send-booking-data';

    @property({ type: String, attribute: 'trip-title' })
    tripTitle: string = '';

    @property({ type: String, attribute: 'variant' })
    variant: Variant = Variant.Default;

    @property({ type: String, attribute: 'x-environment' })
    xEnvironment: Environment = 'production';

    @state()
    viewMode: VIEW_MODE = VIEW_MODE.DEFAULT;

    @state()
    paxCount: number = 1;

    @state()
    salesSite: SalesSiteResponse = {} as SalesSiteResponse;

    @state()
    salesSiteIdentifier: string | undefined = undefined;

    @state()
    protected isSubmitting: boolean = false;

    override opportunityOriginatingObjectTag: string = 'der-tailormade-booking-request';

    private _namePattern: string = "^(?!\\s+$)[A-Za-zÀ-ÖØ-öø-ÿ'\\- ]+$";

    private _emailPattern: string = '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';

    /**
     * Lifecycle method called when the component is added to the DOM.
     */
    override connectedCallback(): void {
        super.connectedCallback();
        this._loadTranslation();
    }

    /**
     * Lifecycle method called when properties change.
     * @param _changedProperties
     * @protected
     */
    protected override async updated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        super.updated(_changedProperties);

        if (_changedProperties.has('offerData') && this.offerData) {
            this.paxCount = this.offerData?.travellers?.adults || 1;
        }
        if (_changedProperties.has('opportunityOwningTeam') && this.opportunityOwningTeam) {
            this.salesSiteIdentifier = this.opportunityOwningTeam;
        }
        if (_changedProperties.has('salesSiteIdentifier') && this.salesSiteIdentifier) {
            await this._getSalesSiteById();
        }
        // If endpoint changes, and we already have an identifier selected, refetch.
        if (_changedProperties.has('salesSiteUrl') && this.salesSiteIdentifier) {
            await this._getSalesSiteById();
        }
        if (_changedProperties.has('confirmation')) {
            this._changeViewMode(this.confirmation ? VIEW_MODE.CONFIRMATION : VIEW_MODE.DEFAULT);
        }
        if (_changedProperties.has('lang')) {
            this._loadTranslation();
        }
    }

    private async _loadTranslation(): Promise<void> {
        await this.loadTranslation(import.meta.url, `i18n/${this.lang}.json`);
    }

    /**
     * Handles the sales site change event.
     * @param event
     * @private
     */
    private _handleSalesSiteChange(event: CustomEvent): void {
        event.stopPropagation();
        this.salesSiteIdentifier = event.detail.salesSite;
    }

    /**
     * Fetches sales site data by ID.
     * @private
     */
    private async _getSalesSiteById(): Promise<void> {
        const url: URL = new URL(this.salesSiteUrl);
        url.searchParams.set('teamId', this.salesSiteIdentifier ?? '');
        url.searchParams.set('lang', this.lang ?? '');

        const response: Response = await fetch(url.toString());
        if (!response.ok) {
            this.error(this.tagName, {
                message: 'Can not load sales-site data',
                teamId: this.salesSiteIdentifier,
            });
        }

        this.salesSite = await response.json();
    }

    /**
     * Scrolls to the confirmation section smoothly after a short delay.
     */
    scrollToConfirmation() {
        setTimeout(() => {
            this.shadowRoot
                ?.querySelector('.m-booking-request__confirmation')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    }

    /**
     * Updates the contact language based on the current language setting.
     */
    _updateContactLanguage() {
        const languageMap: { [key: string]: string } = {
            'fr-CH': '948150001',
            'de-CH': '948150000',
        };
        this.contactLanguage = this.contactLanguage || languageMap[this.lang] || '';
    }

    /**
     * Updates the opportunity source URL to the current page URL if not already set.
     */
    _updateOpportunitySourceUrl() {
        this.opportunitySourceUrl = this.opportunitySourceUrl || (top || window)?.location?.href || '';
    }
    /**
     * Handles the click event on the minus button to decrease the number of travelers.
     * @param e
     */
    minusButtonClick(e: Event) {
        e.preventDefault();
        const minPax = 1;
        const numberInput = this.shadowRoot?.querySelector('.m-booking-request__pax-input') as HTMLInputElement;
        let value = parseInt(numberInput.value, 10);
        if (value > minPax) {
            value--;
            numberInput.value = value + '';
            this.paxCount = value;
        }
    }

    /**
     * Handles the click event on the plus button to increase the number of travelers.
     * @param e
     */
    plusButtonClick(e: Event) {
        e.preventDefault();
        const maxPax = 100;
        const numberInput = this.shadowRoot?.querySelector('.m-booking-request__pax-input') as HTMLInputElement;
        let value = parseInt(numberInput.value, 10);
        if (value < maxPax) {
            value++;
            numberInput.value = value + '';
            this.paxCount = value;
        }
    }

    /**
     * Handles the change event on the pax input to update the pax count.
     * @param e
     */
    paxInputChange(e: Event) {
        e.preventDefault();
        const input = e.target as HTMLInputElement;
        this.paxCount = parseInt(input.value, 10);
    }

    /**
     * Handles the click event on the back button to navigate back to the offer.
     * @param e
     */
    onBackButtonClick(e: Event) {
        e.preventDefault();
        e.stopPropagation();

        const paxInput = this.shadowRoot?.querySelector('der-ui-input[name="OpportunityPax"]') as HTMLInputElement;
        const opportunityPaxValue = paxInput?.value || '1';

        /**
         *
         */
        this.dispatchEvent(
            new CustomEvent('der.confirmation-close', {
                detail: {
                    offerData: this.offerData,
                    productKey: this.productKey,
                    identifier: this.identifier,
                    opportunityPax: opportunityPaxValue,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Sends an analytics event upon form confirmation.
     * @param data
     */
    confirmationAnalyticsEvent(data: unknown) {
        /**
         *
         */
        this.dispatchEvent(
            new CustomEvent('der-analytics', {
                detail: {
                    name: 'form-submitted',
                    element_name: this.tagName.toLowerCase(),
                    element_id: this.id ?? '',
                    action: 'confirmation view after form submit',
                    category: 'confirmation',
                    data: data,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Handles the form submission event.
     * @param e
     */
    onSubmitClick(e: SubmitEvent) {
        e.preventDefault();
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;
        try {
            const form = e.target as HTMLFormElement;
            const formData = new FormData(form);
            const formValues = Object.fromEntries(formData.entries());
            this._updateContactLanguage();
            this._updateOpportunitySourceUrl();

            const json = {
                formName: this.formName,
                ContactLanguage: this.contactLanguage,
                productKey: this.productKey,
                identifier: this.identifier,
                variant: this.variant,
                offerData: this.offerData,
                OpportunityOwningTeam: this.opportunityOwningTeam,
                OpportunityDescription: this.opportunityDescription,
                OpportunityDestination: this.opportunityDestination,
                OpportunityProductId: this.opportunityProductId,
                OpportunitySource: this.opportunitySource,
                OpportunitySourceUrl: this.opportunitySourceUrl,
                OpportunityTitle: this.opportunityTitle,
                OpportunityType: this.opportunityType,
                OpportunityProductBrand: this.opportunityProductBrand,
                OpportunityOriginatingObjectTag: this.opportunityOriginatingObjectTag,
                ...formValues,
            };

            const isFormValid =
                !formData.get('ContactEmailConfirmation') &&
                formData.get('ContactEmail') &&
                formData.get('ContactPhone') &&
                formData.get('ContactFirstName') &&
                formData.get('ContactLastName') &&
                formData.get('ContactGender') &&
                formData.get('OpportunityPax') &&
                (this.variant === Variant.Costs ? formData.get('OpportunityCostConfirmation') : true);

            if (isFormValid) {
                // reduced analytics payload
                const analyticsData = {
                    offerURI: json.offerData.uri || '',
                    roundtripKey: json.productKey || '',
                    name: this.tripTitle,
                    selectedDate: json.offerData?.startDate || '',
                    pax: this.paxCount,
                    pricePerPerson: json.offerData?.price?.amount,
                    email: (formValues['ContactEmail'] as string) || '',
                };

                /**
                 *
                 */
                this.dispatchEvent(
                    new CustomEvent('der-analytics', {
                        detail: {
                            name: 'form-submit',
                            element_name: this.tagName.toLowerCase(),
                            element_id: this.id ?? '',
                            action: 'form submit clicked',
                            category: 'form',
                            data: analyticsData,
                        },
                        bubbles: true,
                        composed: true,
                    })
                );
                /**
                 *
                 */
                this.dispatchEvent(
                    new CustomEvent('form-submitted', {
                        detail: json,
                        bubbles: true,
                        composed: true,
                    })
                );

                fetch(this.submitUrl, {
                    method: 'POST',
                    body: JSON.stringify(json),
                    headers: { 'Content-Type': 'application/json', 'x-environment': this.xEnvironment },
                })
                    .then((response) => {
                        if (response.ok) {
                            this.confirmationAnalyticsEvent(analyticsData);
                            this._changeViewMode(VIEW_MODE.CONFIRMATION);
                            this.scrollToConfirmation();
                        } else {
                            throw new Error(`HTTP-Fehler: ${response.status}`);
                        }
                    })
                    .catch((error: unknown) => {
                        console.log('Error in form submission:', error);
                        this.isSubmitting = false;
                    })
                    .finally(() => {
                        // Unlock after async completes
                        this.isSubmitting = false;
                    });
            } else {
                this._changeViewMode(VIEW_MODE.CONFIRMATION);
                this.scrollToConfirmation();
            }
        } catch (error) {
            this.error(this.tagName, {
                message: 'Error during form submission: ' + error,
                teamId: this.salesSiteIdentifier,
            });
            this.isSubmitting = false;
        }
    }

    /**
     * Handles the input event for textareas to validate against whitespace-only input.
     * @param e
     */
    private _handleInput(e: Event) {
        const customTextarea = e.target as HTMLTextAreaElement;
        const nativeTextarea = customTextarea.shadowRoot?.querySelector('textarea');
        if (nativeTextarea?.value.trim() === '') {
            nativeTextarea.setCustomValidity(this.trans('tFormValidationErrorSpaces'));
        } else {
            nativeTextarea?.setCustomValidity('');
        }
    }

    /**
     * Changes the view mode of the component.
     * @param mode
     * @private
     */
    private _changeViewMode(mode: VIEW_MODE) {
        this.viewMode = mode;
        /**
         *
         */
        this.dispatchEvent(
            new CustomEvent('der.view-mode-change', {
                detail: {
                    mode,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     *
     */
    renderGenderGroup() {
        return html`
            <div class="m-booking-request__input-wrapper">
                <der-ui-radio-group
                    id="genderGroup"
                    name="ContactGender"
                    label="${this.trans('salutation')}"
                    language="${this.lang}"
                    ?required="${true}"
                >
                    <der-ui-radio-button value="2" label="${this.trans('genderFemale')}"></der-ui-radio-button>
                    <der-ui-radio-button value="1" label="${this.trans('genderMale')}"></der-ui-radio-button>
                    <der-ui-radio-button value="3" label="${this.trans('genderDivers')}"></der-ui-radio-button>
                </der-ui-radio-group>
            </div>
        `;
    }

    /**
     *
     */
    renderNameInputs() {
        return html`<div class="m-booking-request__input-wrapper m-booking-request__multiple-inputs-wrapper">
            <der-ui-input
                label="${this.trans('firstName')}"
                name="ContactFirstName"
                ?required="${true}"
                pattern="${this._namePattern}"
            ></der-ui-input>
            <der-ui-input
                label="${this.trans('lastName')}"
                name="ContactLastName"
                ?required="${true}"
                pattern="${this._namePattern}"
            ></der-ui-input>
        </div>`;
    }

    /**
     *
     */
    renderPaxInput() {
        return html`
            <div class="m-booking-request__pax-wrapper">
                <div class="m-booking-request__pax-header">${this.trans('numberOfTravelers')}</div>
                <div class="m-booking-request__pax-counter">
                    <button
                        class="m-booking-request__pax-number-control"
                        aria-label="${this.trans('decrease')}"
                        @click="${this.minusButtonClick}"
                    >
                        <svg width="24" height="24" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                            ${SvgUtility['systemMinus']}
                        </svg>
                    </button>

                    <der-ui-input
                        class="m-booking-request__pax-input"
                        name="OpportunityPax"
                        ?required="${true}"
                        type="number"
                        value="${this.paxCount}"
                        min="1"
                        max="9"
                        @der.change="${(e: Event) => {
                            this.paxInputChange(e);
                        }}"
                    ></der-ui-input>

                    <button
                        class="m-booking-request__pax-number-control"
                        aria-label="${this.trans('increase')}"
                        @click="${this.plusButtonClick}"
                    >
                        <svg width="24" height="24" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                            ${SvgUtility['systemPlus']}
                        </svg>
                    </button>
                </div>
            </div>
        `;
    }

    /**
     *
     */
    renderPaxDataInput() {
        return html` <div class="m-booking-request__input-wrapper">
            <der-ui-textarea
                @input="${this._handleInput}"
                label="${this.trans('paxDataLabel')}"
                name="paxData"
                maxlength="3000"
            ></der-ui-textarea>
        </div>`;
    }

    /**
     *
     */
    renderExtraDataInput() {
        return html` <div class="m-booking-request__input-wrapper">
            <der-ui-textarea
                label="${this.trans('extraDataLabel')}"
                name="extraData"
                ?required="${false}"
                maxlength="3000"
            ></der-ui-textarea>
        </div>`;
    }

    /**
     *
     */
    renderActions() {
        return html`
            <div class="m-booking-request__input-wrapper">
                <div class="m-booking-request__actions-button">
                    <der-ui-button type="submit" variant="primary" ?disabled=${this.isSubmitting}
                        >${this.trans('sendRequest')}
                    </der-ui-button>
                </div>
            </div>
        `;
    }

    /**
     *
     */
    renderPrivacyNotice() {
        return html`<div class="m-booking-request__input-wrapper">
            <div class="m-booking-request__actions-text">
                ${this.trans('privacyNotice')}
                <a href="${this.trans('privacyLink')}" target="_blank">${this.trans('privacyLinkText')}</a>
            </div>
        </div>`;
    }

    /**
     *
     */
    renderEmailConfirmation() {
        return html`<div class="m-booking-request__email-confirmation">
            <label for="emailConfirmation">${this.trans('emailConfirmation')}</label>
            <input
                type="text"
                id="emailConfirmation"
                name="ContactEmailConfirmation"
                tabindex="-1"
                autocomplete="new-password"
            />
        </div>`;
    }

    /**
     *
     */
    renderPhoneMailInput() {
        return html`<div class="m-booking-request__input-wrapper m-booking-request__multiple-inputs-wrapper">
            <der-ui-phone-input
                lang="${this.lang}"
                label="${this.trans('phone')}"
                name="ContactPhone"
                input-placeholder=""
                ?required="${true}"
                validate-message="${this.trans('phoneInvalid')}"
            ></der-ui-phone-input>
            <der-ui-input
                label="${this.trans('email')}"
                name="ContactEmail"
                inputmode="email"
                pattern="${this._emailPattern}"
                ?required="${true}"
                type="email"
            ></der-ui-input>
        </div>`;
    }

    /**
     *
     */
    renderCostsConfirmation() {
        return html`
            <div class="m-booking-request__checkbox-wrapper">
                <der-ui-checkbox name="OpportunityCostConfirmation" ?required="${true}"> </der-ui-checkbox>
                <div class="m-booking-request__checkbox-text">${this.trans('costConfirmation')}</div>
            </div>
        `;
    }

    /**
     *
     */
    renderConfirmation() {
        return html`<div class="m-booking-request__confirmation">
            <div class="m-booking-request__confirmation-body">
                <div class="m-booking-request__confirmation-header">
                    <div class="m-booking-request__confirmation-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">${SvgUtility['systemCheck']}</svg>
                    </div>
                    <div class="m-booking-request__confirmation-text-group">
                        <div class="m-booking-request__confirmation-title">${this.trans('thankYouHeadline')}</div>
                        <div class="m-booking-request__confirmation-description">${this.trans('yourRequest')}</div>
                    </div>
                </div>

                <div class="m-booking-request__trip-info">
                    <div class="m-booking-request__trip-title">${this.tripTitle}</div>
                    <div class="m-booking-request__info-list">
                        <div class="m-booking-request__info-item">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 40 40">
                                ${SvgUtility.systemCalendar()}
                            </svg>
                            <div class="m-booking-request__icon-value">
                                ${formatDateToDDMMYY(this.offerData.startDate)} -
                                ${formatDateToDDMMYY(this.offerData.endDate)}
                            </div>
                        </div>
                        <div class="m-booking-request__info-item">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 40 40">
                                ${SvgUtility['systemGroup']}
                            </svg>
                            <div class="m-booking-request__icon-value">
                                ${this.paxCount}
                                ${this.paxCount === 1 ? this.trans('traveler') : this.trans('travelerPlural')}
                            </div>
                        </div>
                        ${this.salesSite.name
                            ? html`<div class="m-booking-request__info-item">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 40 40">
                                      ${SvgUtility['systemPin']}
                                  </svg>
                                  <div class="m-booking-request__icon-value">
                                      ${this.salesSite.name}, ${this.salesSite.address}, ${this.salesSite.zip}
                                      ${this.salesSite.city}
                                  </div>
                              </div>`
                            : ``}
                    </div>
                </div>

                <div class="m-booking-request__confirmation-footer">
                    <div class="m-booking-request__confirmation-description">
                        ${this.transRichText('requestConfirmationText')}
                    </div>
                    <div class="m-booking-request__confirmation-signature">${this.trans('closingGreeting')}</div>
                </div>
            </div>
            <div class="m-booking-request__confirmation-cta">${this.renderBack()}</div>
        </div>`;
    }

    /**
     *
     */
    renderContactVariantDefault() {
        return html`
            ${this.renderGenderGroup()} ${this.renderNameInputs()} ${this.renderPhoneMailInput()}
            ${this.renderSalesSite()} ${this.renderEmailConfirmation()} ${this.renderPrivacyNotice()}
            ${this.renderActions()}
        `;
    }

    /**
     *
     */
    renderContactVariantWithCostConfirmation() {
        return html`
            ${this.renderGenderGroup()} ${this.renderNameInputs()} ${this.renderPhoneMailInput()}
            ${this.renderSalesSite()} ${this.renderEmailConfirmation()} ${this.renderCostsConfirmation()}
            ${this.renderPrivacyNotice()} ${this.renderActions()}
        `;
    }

    /**
     *
     */
    renderBack() {
        return html`<der-ui-button variant="secondary" @click="${this.onBackButtonClick}"
            >${this.trans('backLink')}</der-ui-button
        >`;
    }

    /**
     *
     */
    renderExtrasCheckboxGroup() {
        return html`<div class="m-booking-request__checkbox-wrapper">
                <der-ui-checkbox name="extraFlight" class="m-booking-request__checkbox"></der-ui-checkbox>
                <div class="m-booking-request__checkbox-text">${this.trans('extraTextFlight')}</div>
            </div>
            <div class="m-booking-request__checkbox-wrapper">
                <der-ui-checkbox name="extraProg" class="m-booking-request__checkbox"></der-ui-checkbox>
                <div class="m-booking-request__checkbox-text">${this.trans('extraTextProg')}</div>
            </div>
            <div class="m-booking-request__checkbox-wrapper">
                <der-ui-checkbox name="extraRoute" class="m-booking-request__checkbox"></der-ui-checkbox>
                <div class="m-booking-request__checkbox-text">${this.trans('extraTextRoute')}</div>
            </div>
            <div class="m-booking-request__checkbox-wrapper">
                <der-ui-checkbox name="extraOther" class="m-booking-request__checkbox"></der-ui-checkbox>
                <div class="m-booking-request__checkbox-text">${this.trans('extraTextOther')}</div>
            </div>`;
    }

    /**
     *
     */
    renderSalesSite() {
        if (!this.opportunityOwningTeam) {
            return html`<div class="m-booking-request__input-wrapper">
                <der-tailormade-booking-request-sales-sites
                    lang="${this.lang}"
                    label="${this.trans('salesSiteLabel')}"
                    name="OpportunityOwningTeam"
                    sales-site-url="${this.salesSiteUrl}"
                    placeholder="${this.trans('select')}"
                    ?required="${true}"
                    @sales-site-selected=${this._handleSalesSiteChange}
                ></der-tailormade-booking-request-sales-sites>
            </div>`;
        }
        return html``;
    }

    override render() {
        return html`
            ${this.viewMode === VIEW_MODE.DEFAULT
                ? html`
                      <div class="m-booking-request">
                          <form name="${this.formName}" @submit="${this.onSubmitClick}">
                              <div class="m-booking-request__form">
                                  <div class="m-booking-request__section">
                                      <div class="m-booking-request__trip-info">
                                          <div class="m-booking-request__trip-title">${this.tripTitle}</div>
                                          <div class="m-booking-request__info-list">
                                              <div class="m-booking-request__info-item">
                                                  <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      width="24"
                                                      height="24"
                                                      viewBox="0 0 40 40"
                                                  >
                                                      ${SvgUtility.systemCalendar()}
                                                  </svg>
                                                  <div class="m-booking-request__icon-value">
                                                      ${formatDateToDDMMYY(this.offerData.startDate)} -
                                                      ${formatDateToDDMMYY(this.offerData.endDate)}
                                                  </div>
                                              </div>
                                              <div
                                                  class="m-booking-request__info-item m-booking-request__info-item--price"
                                              >
                                                  <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      width="24"
                                                      height="24"
                                                      viewBox="0 0 40 40"
                                                  >
                                                      ${SvgUtility['systemSell']}
                                                  </svg>
                                                  <div class="m-booking-request__price-box">
                                                      <div class="m-booking-request__price-value">
                                                          ${this.trans('from')}
                                                          ${this.offerData.price.formattedStringWithCurrency}
                                                      </div>
                                                      <div class="m-booking-request__price-label">
                                                          ${this.trans('perPerson')}
                                                      </div>
                                                  </div>
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                                  <div class="m-booking-request__section">
                                      <div class="m-booking-request__headline">${this.trans('headlinePax')}</div>
                                      <div class="m-booking-request__inputs-wrapper">${this.renderPaxInput()}</div>
                                      <div class="m-booking-request__textarea-wrapper">
                                          ${this.renderPaxDataInput()}
                                      </div>
                                      <div class="m-booking-request__info-wrapper">
                                          <svg
                                              class="m-booking-request__info-icon"
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="24"
                                              height="24"
                                              viewBox="0 0 40 40"
                                          >
                                              ${SvgUtility['systemInfoCircle']}
                                          </svg>
                                          <div class="m-booking-request__info-text">${this.trans('infoTextPax')}</div>
                                      </div>
                                  </div>
                                  <div class="m-booking-request__section">
                                      <div class="m-booking-request__headline">${this.trans('headlineExtras')}</div>
                                      <div class="m-booking-request__checkboxes-wrapper">
                                          ${this.renderExtrasCheckboxGroup()}
                                      </div>
                                      <div class="m-booking-request__textarea-wrapper">
                                          ${this.renderExtraDataInput()}
                                      </div>
                                      <div class="m-booking-request__info-wrapper">
                                          <svg
                                              class="m-booking-request__info-icon"
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="24"
                                              height="24"
                                              viewBox="0 0 40 40"
                                          >
                                              ${SvgUtility['systemInfoCircle']}
                                          </svg>
                                          <div class="m-booking-request__info-text">
                                              ${this.trans('infoTextExtras')}
                                          </div>
                                      </div>
                                  </div>
                                  <div class="m-booking-request__section">
                                      <div class="m-booking-request__headline">${this.trans('headlineContact')}</div>
                                      <div class="m-booking-request__inputs-wrapper">
                                          ${this.variant === Variant.Default
                                              ? this.renderContactVariantDefault()
                                              : this.renderContactVariantWithCostConfirmation()}
                                      </div>
                                  </div>
                              </div>
                          </form>
                      </div>
                  `
                : html` <div class="m-booking-request">${this.renderConfirmation()}</div> `}
        `;
    }
}
